// progression_undo.js — keep a program's progression honest when a finished
// workout is deleted or edited. Pure functions, no React.
//
// completeSession records on the session what afterSession() changed:
//   session.progressionDelta = { pe: { <peId>: { <field>: [from, to] } }, tms: { <key>: [from, to] } } | null
// (fields: targetWeight, schemeState, tm). Deleting the workout reverts those
// changes; editing it replays afterSession() on the "before" values and swaps
// the old result for the new one. A field a later workout already moved is left
// alone — except training maxes, which shift by the same difference (so a typo
// that inflated a 5/3/1 TM is taken back out even after more training).

const FIELDS = ['targetWeight', 'schemeState', 'tm'];

const clone = (v) => (v === undefined ? null : JSON.parse(JSON.stringify(v)));
function same(a, b) {
  if (a === b) return true;
  if ((a == null) && (b == null)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || !a || !b) return false;
  return JSON.stringify(sortKeys(a)) === JSON.stringify(sortKeys(b));
}
function sortKeys(v) {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (!v || typeof v !== 'object') return v;
  const o = {};
  for (const k of Object.keys(v).sort()) if (v[k] !== undefined) o[k] = sortKeys(v[k]);
  return o;
}
const round2 = (x) => Math.round(x * 100) / 100;
function peMap(program) {
  const m = new Map();
  for (const d of (program && program.days) || []) for (const pe of d.exercises || []) if (pe && pe.id != null && !m.has(pe.id)) m.set(pe.id, pe);
  return m;
}

/** What changed between two versions of a program (progression fields only) → delta | null. */
export function progressionDelta(before, after) {
  if (!before || !after) return null;
  const A = peMap(before), B = peMap(after);
  const pe = {}, tms = {};
  for (const [id, b] of B) {
    const a = A.get(id);
    if (!a) continue;
    for (const f of FIELDS) if (!same(a[f], b[f])) (pe[id] ||= {})[f] = [clone(a[f]), clone(b[f])];
  }
  const ta = before.tms || {}, tb = after.tms || {};
  for (const k of new Set([...Object.keys(ta), ...Object.keys(tb)])) if (!same(ta[k], tb[k])) tms[k] = [clone(ta[k]), clone(tb[k])];
  return Object.keys(pe).length || Object.keys(tms).length ? { pe, tms } : null;
}

/** The program as the session found it: every field the delta touched set back to its "from" value. */
export function programBefore(program, delta) {
  if (!program || !delta) return program;
  const days = (program.days || []).map((d) => ({
    ...d,
    exercises: (d.exercises || []).map((pe) => {
      const ch = delta.pe && delta.pe[pe.id];
      if (!ch) return pe;
      const out = { ...pe };
      for (const [f, [from]] of Object.entries(ch)) { if (from == null) delete out[f]; else out[f] = clone(from); }
      return out;
    }),
  }));
  let tms = program.tms;
  if (delta.tms && Object.keys(delta.tms).length) {
    tms = { ...(program.tms || {}) };
    for (const [k, [from]] of Object.entries(delta.tms)) { if (from == null) delete tms[k]; else tms[k] = from; }
  }
  return { ...program, days, ...(tms !== undefined ? { tms } : {}) };
}

/**
 * Swap a session's old progression result (oldDelta) for a new one (newDelta;
 * null = the session no longer exists). Returns { program, changed, notes[] }.
 * newDelta must have been computed against programBefore(program, oldDelta).
 */
export function reconcileProgression(program, oldDelta, newDelta = null) {
  if (!program || (!oldDelta && !newDelta)) return { program, changed: false, notes: [] };
  const notes = [];
  let changed = false;
  const target = (entryOld, entryNew) => {
    const from = entryOld ? entryOld[0] : entryNew[0];
    const oldTo = entryOld ? entryOld[1] : from;
    const newTo = entryNew ? entryNew[1] : from;
    return { from, oldTo, newTo };
  };
  const pick = (cur, { oldTo, newTo }, numericShift) => {
    if (same(cur, oldTo)) return { v: newTo, set: !same(cur, newTo) };
    if (numericShift && typeof cur === 'number' && typeof oldTo === 'number' && typeof newTo === 'number') {
      const v = round2(cur + (newTo - oldTo));
      if (v > 0 && v !== cur) return { v, set: true };
    }
    return { v: cur, set: false };
  };
  const oPe = (oldDelta && oldDelta.pe) || {}, nPe = (newDelta && newDelta.pe) || {};
  const days = (program.days || []).map((d) => ({
    ...d,
    exercises: (d.exercises || []).map((pe) => {
      const fo = oPe[pe.id] || {}, fn = nPe[pe.id] || {};
      const fields = new Set([...Object.keys(fo), ...Object.keys(fn)]);
      if (!fields.size) return pe;
      let out = pe;
      for (const f of fields) {
        const r = pick(pe[f] ?? null, target(fo[f], fn[f]), f === 'tm');
        if (!r.set) continue;
        if (out === pe) out = { ...pe };
        if (r.v == null) delete out[f]; else out[f] = clone(r.v);
        changed = true;
        if (f === 'targetWeight') notes.push(`${pe.name || 'Exercise'}: target back to ${r.v ?? 0} lb`);
      }
      return out;
    }),
  }));
  let tms = program.tms;
  const oT = (oldDelta && oldDelta.tms) || {}, nT = (newDelta && newDelta.tms) || {};
  for (const k of new Set([...Object.keys(oT), ...Object.keys(nT)])) {
    const cur = tms ? tms[k] : undefined;
    const r = pick(cur ?? null, target(oT[k], nT[k]), true);
    if (!r.set) continue;
    tms = { ...(tms || {}) };
    if (r.v == null) delete tms[k]; else tms[k] = r.v;
    changed = true;
    notes.push(`Training max ${k}: ${cur ?? '—'} → ${r.v ?? '—'} lb`);
  }
  if (!changed) return { program, changed: false, notes };
  return { program: { ...program, days, ...(tms !== undefined ? { tms } : {}) }, changed: true, notes };
}

/** Restrict a delta to the fields another delta touched (used when a later workout exists). */
export function limitDelta(delta, to) {
  if (!delta) return null;
  const pe = {}, tms = {};
  for (const [id, ch] of Object.entries(delta.pe || {})) for (const [f, v] of Object.entries(ch)) if (to && to.pe && to.pe[id] && to.pe[id][f]) (pe[id] ||= {})[f] = v;
  for (const [k, v] of Object.entries(delta.tms || {})) if (to && to.tms && to.tms[k]) tms[k] = v;
  return Object.keys(pe).length || Object.keys(tms).length ? { pe, tms } : null;
}

/**
 * Legacy sessions (finished before deltas were recorded) only carry
 * progressedExercises [{ peId, newWeight, increment }]: enough to take a
 * weight bump back if nothing moved it since.
 */
export function legacyDelta(session) {
  const list = Array.isArray(session && session.progressedExercises) ? session.progressedExercises : [];
  const pe = {};
  for (const p of list) {
    if (!p || p.peId == null || !(p.newWeight > 0) || !(p.increment > 0)) continue;
    pe[p.peId] = { targetWeight: [round2(p.newWeight - p.increment), p.newWeight] };
  }
  return Object.keys(pe).length ? { pe, tms: {} } : null;
}
